import React, { useContext, useEffect, useState } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { cloneDeep } from 'lodash-es';
import { useTheme } from '@material-ui/core/styles';
import { FormControlLabel, Switch } from '@material-ui/core';
import { docErrorCodes } from '@mummssoftware/utils/hope';
import { HopeContext } from '../Context/HopeWidgetContext';
import { AgGridTable } from './Table';
import defaults from '../../translations/en.json';
interface HopeItemSetTableProps {
  viewModePrint?: boolean;
  hopeDTOProp: string;
  /** When set (e.g. dashboard validation modal), uses this data instead of HopeContext. */
  rowData?: any[] | null;
}

const frameworkComponents = {};

export const HopeItemExpandedTable = ({
  viewModePrint,
  hopeDTOProp,
  rowData,
}: HopeItemSetTableProps) => {
  const { patientHopeDocData } = useContext(HopeContext);
  const { palette } = useTheme();

  const info = rowData ?? patientHopeDocData;
  const useExternalRows = rowData != null;

  let labelInfo: any[] = [];
  if (info?.length) {
    const infoShow = [...info].filter((i) => i?.balwayshide === 'false');
    labelInfo = infoShow.reduce((seed, item) => {
      // add an extra row on top
      if (item.extraprelabel) {
        const newRow = { ...item };
        newRow.itemdescription = newRow.extraprelabel;
        newRow.itemnumber = '';
        newRow.value = '';
        newRow.formname = '';
        newRow.formavailability = '';
        newRow.formpersoncredentials = '';
        newRow.formpersonfirstname = '';
        newRow.formpersonlastname = '';
        seed.push(newRow);
        seed.push(item);
      } else {
        seed.push(item);
      }
      return seed;
    }, []);
  }

  const validateHOPEItem = (item: any) => item[hopeDTOProp] === 'false';

  let newInfo: any[] = [];
  if (info?.length) {
    if (useExternalRows) {
      newInfo = cloneDeep(labelInfo);
    } else {
      newInfo = cloneDeep(labelInfo).reduce((seed: any, item: any) => {
        if (validateHOPEItem(item)) return seed;

        seed.push(item);

        return seed;
      }, []);
    }
  }

  const hasErrors = newInfo.some((item: any) => item.errorlevel > 0);

  const [showErrorsOnly, setShowErrorsOnly] = useState(hasErrors);

  useEffect(() => {
    setShowErrorsOnly(hasErrors);
  }, [hasErrors]);

  if (!info?.length) {
    return null;
  }

  const displayedInfo = showErrorsOnly
    ? newInfo.filter((item: any) => item.errorlevel > 0)
    : newInfo;

  const getRowStyle = ({ data }: any) => {
    let color;
    let fontWeight;
    let textDecoration;
    if (data.itemnumber === '') {
      fontWeight = 'bolder';
      textDecoration = 'underline';
    } else if (data.errorlevel === 2) {
      color = palette?.error?.main;
    } else if (data.errorlevel === 1) {
      color = palette?.warning?.main;
    }
    return {
      'justify-content': 'start',
      display: 'inline-block',
      position: 'absolute',
      textOverflow: 'ellipsis',
      color: color || '',
      fontWeight: fontWeight || '',
      textDecoration: textDecoration || '',
    };
  };

  const getCellStyle = () => ({
    textOverflow: 'ellipsis',
    display: 'block',
  });

  const columnDefs = [
    {
      headerName: defaults.HopeItem,
      field: 'itemnumber',
      width: 95,
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: getCellStyle,
    },
    {
      headerName: defaults.HopeDesc,
      field: 'itemdescription',
      flex: 1,
      suppressMenu: true,
      cellStyle: getRowStyle,
    },
    {
      headerName: defaults.response,
      field: 'value',
      width: 130,
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: getCellStyle,
    },
    {
      headerName: defaults.auditStatus,
      field: '',
      flex: 1,
      suppressMenu: true,
      cellStyle: getCellStyle,
      valueGetter: ({ data }: any) =>
        (data.errormessage &&
          `${docErrorCodes[`${data.errorlevel}`]} - ${data.errormessage}`) ||
        '',
    },
  ];

  const detailGridOptions = {
    detailRowAutoHeight: true,
    getRowStyle,
    columnDefs,
    headerHeight: 0,
    rowHeight: 26,
    defaultColDef: {
      resizable: true,
    },
  };

  const gridOptions: GridOptions = {
    ...detailGridOptions,
    onGridReady: (params: any) => {
      const gridApi: GridApi = params.api;
      gridApi.sizeColumnsToFit();
      if (viewModePrint) {
        gridOptions.domLayout = 'print';
      }
    },
  };
  return (
    <React.Fragment>
      {hasErrors && (
        <div style={{ padding: '4px 8px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showErrorsOnly}
                onChange={(e) => setShowErrorsOnly(e.target.checked)}
                size="small"
              />
            }
            label={defaults.showErrorsOnly}
          />
        </div>
      )}
      <AgGridTable
        gridOptions={gridOptions}
        rowData={displayedInfo || []}
        frameworkComponents={frameworkComponents}
        multiPatient
      />
    </React.Fragment>
  );
};

export default HopeItemExpandedTable;
